import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  ChevronRightIcon,
  DownloadIcon,
  FileTextIcon,
  Menu,
  Info,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import MenuBar from "@/components/MenuBar";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import {
  Filing,
  FilingResponse,
  FilingStatus,
  FilingType,
  TransmissionStatus,
} from "@/types/filings";
import { User } from "@/types/user";
import DashboardContainer from "@/components/layout/DashboardContainer";
import { FilingTransmissionModal } from "@/components/filings/FilingTransmissionModal";
import { FilingTransmissionTilModal } from "@/components/filings/FilingTransmissionTilModal";
import { FilingTransmissionStatus } from "@/components/filings/FilingTransmissionStatus";
import { FilingTransmissionHistoryComponent } from "@/components/filings/FilingTransmissionHistory";

export default function FilingsGrid() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [filings, setFilings] = useState<Filing[]>([]);
  const [selectedFiling, setSelectedFiling] = useState<Filing | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showTilTransmissionModal, setShowTilTransmissionModal] = useState(false);

  const isNanny = user?.userType === "nanny";

  const checkSuperUserStatus = async () => {
    try {
      const response = await ApiService.request<{ users: User[] }>(
        EndpointKeys.FETCH_ALL_USERS,
      );
      setUsers(response.users);
      setIsSuperUser(true);
    } catch (error) {
      setIsSuperUser(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isNanny) {
      checkSuperUserStatus();
    } else {
      setIsLoading(false);
    }
  }, [isNanny]);

  useEffect(() => {
    if (!isLoading) {
      fetchFilings();
    }
  }, [selectedUserId, isNanny, isLoading]);

  const fetchFilings = async () => {
    try {
      const queryParams = {
        ...(selectedUserId ? { userId: selectedUserId } : {}),
        ...(isNanny ? { filingTypes: "P45,P60" } : {}),
        includeDraftFilings: "true", // Include draft filings to show FPS previews
      };

      const response = await ApiService.request<FilingResponse>(
        EndpointKeys.FETCH_EMPLOYER_FILINGS,
        {
          queryParams,
        },
      );
      const filteredFilings = response.filings.filter(
        (filing) => filing.filingType && filing.filingType.trim() !== "",
      );
      setFilings(filteredFilings);
    } catch (error) {
      console.error("Error fetching filings:", error);
      setFilings([]);
    }
  };

  const getStatusBadgeVariant = (
    status: FilingStatus,
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status.toLowerCase()) {
      case "pending":
        return "secondary";
      case "draft":
        return "outline"; // Use outline for draft filings to distinguish them
      case "sent":
        return "default";
      case "success":
        return "default";
      case "failed":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getTransmissionStatusBadgeVariant = (
    status: TransmissionStatus,
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "DRAFT":
        return "outline";
      case "SUBMITTED":
        return "secondary";
      case "COMPLETED":
        return "default";
      case "FAILED":
        return "destructive";
      default:
        return "outline";
    }
  };

  const handleRowClick = (filing: Filing) => {
    setSelectedFiling(filing);
    setIsDrawerOpen(true);
  };

  const handleDownloadDocument = async (documentId: number) => {
    try {
      await ApiService.downloadBlob(
        EndpointKeys.FETCH_EMPLOYER_FILING_DOCUMENT,
        {
          urlParams: { id: documentId },
        },
      );
    } catch (error) {
      console.error("Error downloading document:", error);
    }
  };

  const isAllowedFilingType = (type: FilingType): boolean => {
    if (!isNanny) return true;
    return type === "P45" || type === "P60";
  };

  const handleRefreshFiling = async (
    filingId: number,
    isTest: boolean = false,
  ) => {
    setIsRefreshing(true);
    try {
      await ApiService.request(EndpointKeys.REFRESH_EMPLOYER_FILING, {
        method: "POST",
        urlParams: { id: filingId },
        body: { isTest },
      });

      // Refresh the filings list after successful refresh
      await fetchFilings();

      // Update the selected filing if it's currently open
      if (selectedFiling && selectedFiling.id === filingId) {
        const updatedFiling = filings.find((f) => f.id === filingId);
        if (updatedFiling) {
          setSelectedFiling(updatedFiling);
        }
      }

      toast({
        title: "Success",
        description: "Filing has been refreshed successfully.",
      });
    } catch (error) {
      console.error("Error refreshing filing:", error);
      toast({
        title: "Error",
        description: "Failed to refresh filing. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const HeaderContent = () => (
    <>
      {isSuperUser && !isNanny && (
        <div className="w-full md:w-64">
          <Select
            value={selectedUserId}
            onValueChange={(value) => setSelectedUserId(value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select User" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current">Current User</SelectItem>
              {users.map((user) => (
                <SelectItem key={user.id} value={user.id.toString()}>
                  {user.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </>
  );

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <DashboardContainer
      title={isNanny ? "My Documents" : "HMRC Filings"}
      headerContent={<HeaderContent />}
    >
      <Alert className="mb-4">
        <Info className="h-4 w-4 text-primary" />
        <AlertTitle>Document Management</AlertTitle>
        <AlertDescription>
          Browse and access all your HMRC filings and documents (FPS, EPS, P45,
          P60, P11, etc.). Hirelo automatically generates and securely stores
          these documents for your records, ensuring compliance with HMRC
          requirements. You can download any document by clicking on a row for
          more details.
        </AlertDescription>
      </Alert>

      <ScrollArea className="w-full h-[calc(100vh-12rem)] md:h-[calc(100vh-8rem)]">
        <div className="w-full rounded-md border bg-white">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                {isSuperUser && !isNanny && <TableHead>Email</TableHead>}
                <TableHead>Coverage Period</TableHead>
                <TableHead>Document Type</TableHead>
                <TableHead>File Status</TableHead>
                <TableHead>Transmission Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filings
                .filter((filing) => isAllowedFilingType(filing.filingType))
                .map((filing) => (
                  <TableRow
                    key={filing.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(filing)}
                  >
                    {isSuperUser && !isNanny && (
                      <TableCell>
                        {
                          users.find((u) => u.id === parseInt(selectedUserId))
                            ?.email
                        }
                      </TableCell>
                    )}
                    <TableCell className="whitespace-nowrap">
                      {format(
                        new Date(filing.coveragePeriodStart + "T00:00:00"),
                        "PP",
                      )}{" "}
                      -{" "}
                      {format(
                        new Date(filing.coveragePeriodEnd + "T00:00:00"),
                        "PP",
                      )}
                    </TableCell>
                    <TableCell>{filing.filingType}</TableCell>
                    <TableCell>
                      <Badge
                        variant={getStatusBadgeVariant(
                          filing.latestVersion.status,
                        )}
                      >
                        {filing.latestVersion.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {filing.latestVersion.transmissionStatus ? (
                        <Badge
                          variant={getTransmissionStatusBadgeVariant(
                            filing.latestVersion.transmissionStatus,
                          )}
                        >
                          {filing.latestVersion.transmissionStatus}
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not submitted</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <ChevronRightIcon className="h-4 w-4" />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </ScrollArea>

      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetContent className="w-[600px] sm:w-[900px] sm:max-w-[900px]">
          {selectedFiling && (
            <FilingDetails
              filing={selectedFiling}
              onDownload={handleDownloadDocument}
              onRefresh={handleRefreshFiling}
              isRefreshing={isRefreshing}
              isSuperUser={isSuperUser}
              isNanny={isNanny}
              onShowTilModal={() => setShowTilTransmissionModal(true)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* TIL Transmission Modal */}
      {selectedFiling && selectedFiling.latestVersion.documents.length > 0 && (
        <FilingTransmissionTilModal
          filingDocumentId={selectedFiling.latestVersion.documents[0]?.id || 0}
          isOpen={showTilTransmissionModal}
          onClose={() => setShowTilTransmissionModal(false)}
          onSubmitSuccess={async (transmissionId) => {
            // Refresh the filings list after successful TIL submission
            await fetchFilings();
            
            // Update the selected filing if it's currently open
            if (selectedFiling) {
              const updatedFiling = filings.find((f) => f.id === selectedFiling.id);
              if (updatedFiling) {
                setSelectedFiling(updatedFiling);
              }
            }
            
            setShowTilTransmissionModal(false);
            
            toast({
              title: "Success",
              description: "TIL filing has been submitted successfully.",
            });
          }}
        />
      )}
    </DashboardContainer>
  );
}

const FilingDetails: React.FC<{
  filing: Filing;
  onDownload: (documentId: number) => Promise<void>;
  onRefresh: (filingId: number, isTest?: boolean) => Promise<void>;
  isRefreshing: boolean;
  isSuperUser: boolean;
  isNanny: boolean;
  onShowTilModal: () => void;
}> = ({
  filing,
  onDownload,
  onRefresh,
  isRefreshing,
  isSuperUser,
  isNanny,
  onShowTilModal,
}) => {
  const { user } = useAuth();
  const [showTransmissionModal, setShowTransmissionModal] = useState(false);
  const [activeTransmissionId, setActiveTransmissionId] = useState<
    number | null
  >(null);

  const getStatusBadgeVariant = (
    status: FilingStatus,
  ): "default" | "secondary" | "destructive" | "outline" => {
    switch (status.toLowerCase()) {
      case "pending":
        return "secondary";
      case "draft":
        return "outline"; // Use outline for draft filings to distinguish them
      case "sent":
        return "default";
      case "success":
        return "default";
      case "failed":
        return "destructive";
      default:
        return "outline";
    }
  };

  const canRefresh = isSuperUser && !isNanny;

  return (
    <div className="flex flex-col h-full">
      <SheetHeader className="pb-6">
        <div className="flex items-start justify-between gap-4">
          <SheetTitle className="text-left">Document Details</SheetTitle>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            {canRefresh && (
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRefresh(filing.id, false)}
                  disabled={isRefreshing}
                  data-testid="refresh-filing-button"
                  className="w-full sm:w-auto"
                >
                  <RefreshCw
                    className={`mr-2 h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
                  />
                  {isRefreshing ? "Refreshing..." : "Refresh"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRefresh(filing.id, true)}
                  disabled={isRefreshing}
                  data-testid="refresh-filing-test-button"
                  className="w-full sm:w-auto"
                >
                  <RefreshCw
                    className={`mr-2 h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
                  />
                  {isRefreshing ? "Refreshing..." : "Refresh (Test)"}
                </Button>
              </div>
            )}
            {(() => {
              console.log('DEBUG - Submit button conditions:', {
                isSuperUser,
                filingType: filing.filingType,
                status: filing.latestVersion.status,
                statusLower: filing.latestVersion.status.toLowerCase(),
                shouldShow: isSuperUser && filing.filingType === "FPS" && filing.latestVersion.status.toLowerCase() === "pending"
              });
                             return isSuperUser &&
                 filing.filingType === "FPS" &&
                 (filing.latestVersion.status.toLowerCase() === "pending" || 
                  filing.latestVersion.status.toUpperCase() === "PENDING");
            })() && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => {
                      console.log('Regular submit button clicked');
                      setShowTransmissionModal(true);
                    }}
                    variant="outline"
                    size="sm"
                    data-testid="manual-submit-button"
                    className="w-full sm:w-auto"
                  >
                    Submit to HMRC
                  </Button>
                  <Button
                    onClick={() => {
                      console.log('TIL button clicked');
                      onShowTilModal();
                    }}
                    variant="outline"
                    size="sm"
                    data-testid="manual-submit-til-button"
                    className="w-full sm:w-auto bg-orange-200 border-orange-400 text-orange-800 hover:bg-orange-300 font-semibold"
                  >
                    Submit to HMRC (TIL)
                  </Button>
                </div>
              )}
          </div>
        </div>
      </SheetHeader>

      <ScrollArea className="flex-1 pr-4">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Document Information</h3>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <span className="font-medium text-sm text-muted-foreground">
                  Coverage Period:
                </span>
                <div className="mt-1">
                  {format(
                    new Date(filing.coveragePeriodStart + "T00:00:00"),
                    "dd/MM/yyyy",
                  )}{" "}
                  -{" "}
                  {format(
                    new Date(filing.coveragePeriodEnd + "T00:00:00"),
                    "dd/MM/yyyy",
                  )}
                </div>
              </div>
              {!isNanny && (
                <div>
                  <span className="font-medium text-sm text-muted-foreground">
                    Employer:
                  </span>
                  <div className="mt-1">
                    {filing.employerName} ({filing.employerHMRCRef})
                  </div>
                </div>
              )}
              <div>
                <span className="font-medium text-sm text-muted-foreground">
                  Document Type:
                </span>
                <div className="mt-1">{filing.filingType}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h3 className="text-lg font-semibold">Version History</h3>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filing.allVersions.map((version) => (
                  <div
                    key={version.id}
                    className="p-4 border rounded-lg space-y-3"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          Version {version.version}
                        </span>
                        <Badge variant={getStatusBadgeVariant(version.status)}>
                          {version.status}
                        </Badge>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {format(
                          new Date(version.fulfillmentDate),
                          "dd/MM/yyyy",
                        )}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {version.documents.map((doc) => (
                        <Button
                          key={doc.id}
                          variant="outline"
                          size="sm"
                          onClick={() => onDownload(doc.id)}
                          disabled={!doc.hasContents}
                          className="flex-shrink-0"
                        >
                          <DownloadIcon className="mr-2 h-4 w-4" />
                          {doc.documentType}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* HMRC Transmission History - Only for superusers */}
          {isSuperUser && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">HMRC Transmission History</h3>
                <p className="text-sm text-muted-foreground">
                  Complete history of all HMRC submission attempts, responses, and error details
                </p>
              </CardHeader>
              <CardContent>
                <FilingTransmissionHistoryComponent filingId={filing.latestVersion.id} />
              </CardContent>
            </Card>
          )}

          {activeTransmissionId && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold">
                  HMRC Submission Status
                </h3>
              </CardHeader>
              <CardContent>
                <FilingTransmissionStatus
                  transmissionId={activeTransmissionId}
                  onComplete={() => onRefresh(filing.id)}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>

      {filing.latestVersion.documents.length > 0 && (
        <FilingTransmissionModal
          filingDocumentId={filing.latestVersion.documents[0]?.id || 0}
          isOpen={showTransmissionModal}
          onClose={() => setShowTransmissionModal(false)}
          onSubmitSuccess={(transmissionId) => {
            setActiveTransmissionId(transmissionId);
            setShowTransmissionModal(false);
          }}
        />
      )}
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="flex h-screen">
    <div className="w-80">
      <MenuBar />
    </div>
    <div className="flex-1 p-8">
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    </div>
  </div>
);
